import { useState, useEffect, useMemo } from "react";
import { fetchVendors, fetchProducts } from "../../../API_Handler";
import { MagnifyingGlassIcon, ButtonExpandDropdownIconWhite, ButtonCollapseDropdownIconWhite, DownloadIcon } from "../../../../Constants/Icons";
import { adminPortalVendorSortOptions } from "../../../../Constants/Data";
import VendorModal from "./VendorModal";
import { exportVendorsToPDF, exportVendorsToCSV } from "./VendorExportUtil";

const Vendors = ({ vendorsList, productsList }) => {

    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        const fetchVendorsData = async () => {
            setLoading(true);
            const fetchedVendors = await fetchVendors();
            setVendors(Array.isArray(fetchedVendors) ? fetchedVendors : []);
            setLoading(false);
        };

        if (vendorsList && Array.isArray(vendorsList)) {
            setVendors(vendorsList);
        } else {
            fetchVendorsData();
        }

        const fetchProductsData = async () => {
            setLoading(true);
            const fetchedProducts = await fetchProducts();
            setProducts(Array.isArray(fetchedProducts) ? fetchedProducts : []);
            setLoading(false);
        };

        if (productsList && Array.isArray(productsList)) {
            setProducts(productsList);
        } else {
            fetchProductsData();
        }
    }, [vendorsList, productsList]);

    const [searchTerm, setSearchTerm] = useState("");

    const handleChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const [sortOption, setSortOption] = useState(adminPortalVendorSortOptions[0]);
    const [vendorFilters, setVendorFilters] = useState(["all"]);

    const handleSortOptionChange = (option) => {
        setSortOption(option);
    };

    const handleCheckboxChange = (value) => {
        setVendorFilters(prev => {
            if (value === "all") {
                return ["all"];
            }
            const has = prev.includes(value);
            const base = prev.filter(v => v !== "all");
            const next = has
                ? base.filter(v => v !== value)
                : [...base, value];

            return next.length > 0 ? next : ["all"];
        });
    };

    const displayedVendors = useMemo(() => {
        if (!Array.isArray(vendors)) {
            return [];
        }

        let list = [...vendors];

        if (searchTerm.trim() !== "") {
            const q = searchTerm.toLowerCase();
            list = list.filter(v =>
                (v.company_name || "").toLowerCase().includes(q) ||
                (v.vendor_bio || "").toLowerCase().includes(q) ||
                (v.address || "").toLowerCase().includes(q) ||
                (v.website || "").toLowerCase().includes(q) ||
                (v.year_founded || "").toLowerCase().includes(q) ||
                (v.service_specialties || "").toLowerCase().includes(q) ||
                (v.service_description || "").toLowerCase().includes(q) ||
                (v.serve_plan || "").toLowerCase().includes(q) ||
                (v.contact_name || "").toLowerCase().includes(q) ||
                (v.contact_email || "").toLowerCase().includes(q) ||
                (v.contact_phone_number || "").includes(q) ||
                (v.date_registered || "").includes(q) ||
                String(v.id).includes(q)
            );
        } else if (!vendorFilters.includes("all")) {
            list = list.filter(v => vendorFilters.includes(v.approval_status));
        }

        switch (sortOption) {
            case "Newest":
                list.sort((a, b) => b.id - a.id);
                break;
            case "Oldest":
                list.sort((a, b) => a.id - b.id);
                break;
            case "Alphabetical (A-Z)":
                list.sort((a, b) =>
                    (a.company_name || "").localeCompare(b.company_name || "")
                );
                break;
            case "Alphabetical (Z-A)":
                list.sort((a, b) =>
                    (b.company_name || "").localeCompare(a.company_name || "")
                );
                break;
        }

        return list;
    }, [vendors, vendorFilters, sortOption, searchTerm]);

    return (
        <div>
            {loading ?
                <div className="w-full h-screen flex flex-col items-center justify-center">
                    <div className="spinner-border" role="status"> </div>
                    <p className="mt-[12px] text-center font-proximanova text-[18px] text-[#505050] font-[400]">Loading...</p>
                </div>
                :
                <div className="w-full h-[calc(100vh-60px)] flex flex-col overflow-y-auto">
                    <div className="w-full h-full max-h-12 flex flex-row gap-[24px] items-center justify-between">
                        <div className="w-full px-[24px] gap-[16px] flex flex-row items-center bg-white border-exploreSearchBorder border-1 rounded-full relative">
                            <div className="w-full flex justify-center">
                                <input
                                    type="text"
                                    className="w-full h-10 focus:outline-none font-proximanova text-[18px] text-[#505050] mb-0"
                                    placeholder="Search for vendors"
                                    value={searchTerm}
                                    onChange={handleChange}
                                />
                                <button className="w-fit">
                                    <MagnifyingGlassIcon />
                                </button>
                            </div>
                        </div>

                        <div className="relative h-full">
                            <button
                                className="w-fit h-full max-h-12 px-[24px] flex flex-row items-center bg-exploreSearchBorder rounded-full text-white font-proximanova text-[18px]"
                                onClick={() => setIsSortModalOpen(!isSortModalOpen)}
                            >
                                Filters {isSortModalOpen ? <ButtonExpandDropdownIconWhite /> : <ButtonCollapseDropdownIconWhite />}
                            </button>

                            {isSortModalOpen && (
                                <div className="absolute right-0 top-full mt-2 w-fit flex flex-row justify-between border-[1px] border-charmHover bg-white z-10">
                                    <div className="w-fit flex flex-col">
                                        <p className="w-full px-[16px] font-proximanova font-[500] text-[18px] text-[#505050] text-left mb-0">Sort By:</p>
                                        {adminPortalVendorSortOptions.map(option => (
                                            <button
                                                key={option}
                                                onClick={() => handleSortOptionChange(option)}
                                                className={`w-full px-[16px] py-[2px] font-proximanova font-[400] text-[18px] text-[#505050] whitespace-nowrap hover:bg-charmHover hover:text-white ${option === sortOption ? 'bg-charmHover text-white' : ''
                                                    }`}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="w-fit flex flex-col">
                                        <p className="w-full px-[16px] font-proximanova font-[500] text-[18px] text-[#505050] text-left mb-0">Show:</p>

                                        <label className="w-full px-[16px] py-[2px] flex items-center gap-2 text-[#505050] text-[18px] font-proximanova cursor-pointer whitespace-nowrap">
                                            <input type="checkbox" value="all" className="form-checkbox accent-charmHover"
                                                checked={vendorFilters.includes("all")}
                                                onChange={() => handleCheckboxChange("all")} />
                                            All Vendors
                                        </label>

                                        <label className="w-full px-[16px] py-[2px] flex items-center gap-2 text-[#505050] text-[18px] font-proximanova cursor-pointer whitespace-nowrap">
                                            <input type="checkbox" value="PENDING" className="form-checkbox accent-charmHover"
                                                checked={vendorFilters.includes("PENDING")}
                                                onChange={() => handleCheckboxChange("PENDING")} />
                                            Pending
                                        </label>

                                        <label className="w-full px-[16px] py-[2px] flex items-center gap-2 text-[#505050] text-[18px] font-proximanova cursor-pointer whitespace-nowrap">
                                            <input type="checkbox" value="APPROVED" className="form-checkbox accent-charmHover"
                                                checked={vendorFilters.includes("APPROVED")}
                                                onChange={() => handleCheckboxChange("APPROVED")} />
                                            Approved
                                        </label>

                                        <label className="w-full px-[16px] py-[2px] flex items-center gap-2 text-[#505050] text-[18px] font-proximanova cursor-pointer whitespace-nowrap">
                                            <input type="checkbox" value="REJECTED" className="form-checkbox accent-charmHover"
                                                checked={vendorFilters.includes("REJECTED")}
                                                onChange={() => handleCheckboxChange("REJECTED")} />
                                            Rejected
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            className="w-fit h-full max-h-12 px-[24px] flex flex-row items-center bg-exploreSearchBorder rounded-full text-white font-proximanova text-[13px] hover:bg-charmHover transition"
                            onClick={() => exportVendorsToCSV(vendorsList)}
                        >
                            Export All Vendors
                            <DownloadIcon />
                        </button>
                    </div>

                    <div className="mt-[16px] flex flex-col bg-white mb-[16px]">
                        <div className="w-full px-[16px] flex flex-row">
                            <p className="min-w-[60px] max-w-[60px] pr-[16px] font-proximanova text-[18px] text-[#505050] border-r-[1px] border-[#a0a0a0] mb-0">ID</p>
                            <p className="w-full pl-[16px] font-proximanova text-[18px] text-[#505050] text-left font-[600] mb-0"> Vendor Name</p>
                            <div className="flex flex-row justify-between">
                                <p className="w-[160px] pl-[16px] font-proximanova text-[18px] text-[#505050] text-left font-[600] mb-0 border-l-[1px] pl-[16px] border-[#a0a0a0]"> Activity</p>
                                <p className="w-[140px] pl-[16px] font-proximanova text-[18px] text-[#505050] text-left font-[600] mb-0 border-l-[1px] pl-[16px] border-[#a0a0a0]"> Status</p>
                                <p className="w-[80px] pl-[16px] font-proximanova text-[18px] text-[#505050] text-left font-[600] mb-0 border-l-[1px] pl-[16px] border-[#a0a0a0]"> Action</p>
                            </div>
                        </div>
                        {displayedVendors.length > 0 ?
                            displayedVendors.map((vendor, idx) => (
                                <VendorModal
                                    key={vendor.id}
                                    selectedVendor={vendor}
                                    backgroundColor={idx % 2 === 0 ? 'bg-[#e6e6e6]' : 'bg-[#f5f5f5]'}
                                    vendorProducts={products.filter(
                                        product => product.vendor && product.vendor.id === vendor.id
                                    )}
                                />
                            ))
                            : (
                                <p className="py-[64px] text-center border-t-[1px] border-[#a0a0a0] text-[#888] font-proximanova text-[18px] mb-0">
                                    No vendors match your filters.
                                </p>
                            )
                        }
                    </div>

                </div>

            }
        </div >
    );
};

export default Vendors;